import { useEffect, useId, useRef, useState, type JSX, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import './components.css';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hasAttribute('hidden'));
}

export interface DialogProps {
  title: string;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  height?: number;
  /** Remove body padding and scrolling so the content can lay itself out. */
  flush?: boolean;
  resizable?: boolean;
  movable?: boolean;
  ownerDocument?: Document;
  suspended?: boolean;
  standalone?: boolean;
}

/**
 * Modal dialog: traps Tab focus, closes on Escape, restores focus on unmount.
 * A press on the backdrop does not close it, so a stray click cannot discard edits.
 */
export function Dialog({ title, onClose, children, footer, width = 520, height, flush, resizable, movable, ownerDocument = document, suspended, standalone }: DialogProps): JSX.Element {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{left:number;top:number} | null>(null);
  const [maximized, setMaximized] = useState(false);
  const [restoredSize, setRestoredSize] = useState<{width:number;height:number} | null>(null);
  const restore = useRef<DOMRect | null>(null);
  const drag = useRef<{id:number;x:number;y:number;left:number;top:number} | null>(null);
  const canMove = movable && !standalone;
  const constrain = (left:number, top:number, rect=panel.current!.getBoundingClientRect()): {left:number;top:number} => {
    const win = ownerDocument.defaultView!;
    return {left:Math.max(16,Math.min(left,win.innerWidth-rect.width-16)),top:Math.max(16,Math.min(top,win.innerHeight-rect.height-16))};
  };
  const toggleMaximized = (): void => {
    if (!maximized) {restore.current=panel.current!.getBoundingClientRect();setMaximized(true);}
    else {
      const rect=restore.current!;
      setRestoredSize({width:rect.width,height:rect.height});
      setPosition(constrain(rect.left,rect.top,rect));setMaximized(false);
    }
  };
  useEffect(() => {
    if (!canMove) return;
    const resized = (): void => {if(panel.current)setPosition(p=>p?constrain(p.left,p.top):null);};
    ownerDocument.defaultView?.addEventListener('resize',resized);
    const observer=typeof ResizeObserver==='undefined'?null:new ResizeObserver(resized);
    if(panel.current)observer?.observe(panel.current);
    return ()=>{ownerDocument.defaultView?.removeEventListener('resize',resized);observer?.disconnect();};
  }, [canMove, ownerDocument]);

  useEffect(() => {
    if (suspended) return;
    const previous = ownerDocument.activeElement as HTMLElement | null;
    const node = panel.current;
    if (node) {
      const preferred = node.querySelector<HTMLElement>('[data-autofocus]') ?? focusableWithin(node)[1] ?? node;
      preferred.focus();
    }
    return () => previous?.focus();
  }, [ownerDocument, suspended]);

  // Focus can end up outside the dialog (the focused control was removed, the backdrop was pressed).
  // Escape and Tab must keep working then, for the top-most dialog only.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (suspended) return;
    const onDocumentKey = (event: globalThis.KeyboardEvent): void => {
      const node = panel.current;
      if (!node || event.defaultPrevented || (event.key !== 'Escape' && event.key !== 'Tab')) return;
      const active = ownerDocument.activeElement;
      if (active?.closest('.ui-dialog, .ui-menu')) return;
      const dialogs = ownerDocument.querySelectorAll('.ui-dialog');
      if (dialogs[dialogs.length - 1] !== node) return;
      event.preventDefault();
      if (event.key === 'Escape') close.current();
      else (focusableWithin(node)[0] ?? node).focus();
    };
    ownerDocument.addEventListener('keydown', onDocumentKey);
    return () => ownerDocument.removeEventListener('keydown', onDocumentKey);
  }, [ownerDocument, suspended]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (suspended || event.defaultPrevented) return;
    if (event.key === 'Escape') {
      event.stopPropagation();
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !panel.current) return;
    const items = focusableWithin(panel.current);
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = ownerDocument.activeElement;
    if (event.shiftKey && (active === first || active === panel.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="ui-overlay"
      style={suspended ? {display:'none'} : undefined}
      onMouseDown={(event) => {
        // A press on the backdrop must not pull focus out of the dialog.
        if (event.target === event.currentTarget) event.preventDefault();
      }}
    >
      <div
        ref={panel}
        className="ui-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={standalone ? {width:'100vw',height:'100dvh',maxWidth:'none',maxHeight:'none',borderRadius:0} : { width:maximized?'calc(100vw - 32px)':restoredSize?.width??width, height:maximized?'calc(100dvh - 32px)':restoredSize?.height??height, ...(resizable ? {resize:maximized?'none':'both',overflow:'hidden',minWidth:'min(400px, calc(100vw - 32px))',minHeight:'min(300px, calc(100vh - 32px))'} : {}), ...(maximized?{position:'fixed',left:16,top:16}:position?{position:'fixed',...position}:{}) }}
        onPointerDownCapture={(event)=>{
          if(canMove&&!maximized&&event.target===panel.current){const rect=panel.current!.getBoundingClientRect();setPosition({left:rect.left,top:rect.top});}
        }}
        onKeyDown={onKeyDown}
      >
        <header className="ui-dialog-header"
          style={canMove?{cursor:maximized?'default':'move',touchAction:'none',userSelect:'none'}:undefined}
          onDoubleClick={(event)=>{if(canMove&&resizable&&!(event.target as HTMLElement).closest('button'))toggleMaximized();}}
          onPointerDown={(event)=>{
            if(!canMove||maximized||event.button!==0||(event.target as HTMLElement).closest('button'))return;
            event.preventDefault();const rect=panel.current!.getBoundingClientRect();
            drag.current={id:event.pointerId,x:event.clientX,y:event.clientY,left:rect.left,top:rect.top};
            setPosition({left:rect.left,top:rect.top});event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event)=>{const d=drag.current;if(d&&d.id===event.pointerId)setPosition(constrain(d.left+event.clientX-d.x,d.top+event.clientY-d.y));}}
          onPointerUp={()=>{drag.current=null;}}
          onPointerCancel={()=>{drag.current=null;}}
          onLostPointerCapture={()=>{drag.current=null;}}
        >
          <h2 id={titleId} className="ui-dialog-title">
            {title}
          </h2>
          {canMove&&resizable?<button type="button" className="ui-icon-btn" aria-label={maximized?'Restore':'Maximize'} title={maximized?'Restore':'Maximize'} onClick={toggleMaximized}>{maximized?'❐':'□'}</button>:null}
          <button type="button" className="ui-icon-btn" aria-label="Close" onClick={onClose}>
            <Icon name="close" />
          </button>
        </header>
        <div className={flush ? 'ui-dialog-body flush' : 'ui-dialog-body'}>{children}</div>
        {footer ? <footer className="ui-dialog-footer">{footer}</footer> : null}
      </div>
    </div>,
    ownerDocument.body,
  );
}
